import { Dispatch, SetStateAction, forwardRef, useEffect, useState } from 'react';
import ConnectWithoutContactIcon from '@mui/icons-material/ConnectWithoutContact';
import {
  Avatar,
  Box,
  Button,
  IconButton,
  Paper,
  Popover,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { getUser, getUserSettings } from '@juno/client-api';
import { CommunityUser, JunoUser, RoleEnum, UserSettings } from '@juno/client-api/model';
import {
  FEATURE_FLAG,
  useFeatureFlagWithUser,
  useIsUserOnline,
  usePrivateCall,
  useSettings,
} from '@juno/utils';
import { WithOptionalTooltip } from '../WithOptionalTooltip';

interface UserQuickViewProps {
  anchorEl: HTMLElement | null;
  setAnchorEl?: Dispatch<SetStateAction<HTMLElement | null>>;
  userId: string;
  open: boolean;
  platformId?: string;
  onViewProfile: (userId: string) => void;
  onMessageUser: (userId: string) => void;
  dialogRef?: React.RefObject<HTMLDivElement>;
}

const UserQuickView = forwardRef<any, UserQuickViewProps>((props, ref) => {
  const { anchorEl, userId, dialogRef, onViewProfile, setAnchorEl } = props;
  const { platform, featureConfigs } = useSettings();

  const { enableEmails = false, enablePrivateMeetings } = featureConfigs?.user_and_profile || {};
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<JunoUser>({} as JunoUser);
  const { value: privateMeetingsValue } = useFeatureFlagWithUser(FEATURE_FLAG.PRIVATE_MEETINGS);
  const [userSettings, setUserSettings] = useState<UserSettings[]>([]);

  const fetchUserDetails = async () => {
    if (!platform) return;
    setLoading(true);

    const userFetch = await getUser(platform.id, userId);
    setUser(userFetch);

    if (user?.id) {
      const userSettingsFetch = await getUserSettings(platform?.default_site?.id || '', {
        filter: { user__id: user?.id },
      });
      setUserSettings(userSettingsFetch);
    }

    setLoading(false);
  };

  const isPrivateUser = userSettings?.find((setting) => setting.user === userId)?.private_profile;

  useEffect(() => {
    if (anchorEl) {
      fetchUserDetails();
    }
  }, [anchorEl, dialogRef, user?.id]);

  return (
    <Popover
      id='user-quick-view'
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      disableScrollLock
      sx={{ boxShadow: 0, '.MuiPopover-paper': { boxShadow: 5, borderRadius: 2 } }}
      BackdropProps={{
        // we use this instead of onClose to prevent event bubbling
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
          setAnchorEl && setAnchorEl(null);
        },
      }}
    >
      <Paper
        sx={{
          padding: '16px',
        }}
      >
        {loading ? (
          <>
            <Skeleton variant='circular' width={40} height={40} />
            <Skeleton variant='text' width={140} height={40} />
            <Skeleton variant='text' width={180} height={20} />
            <Skeleton variant='text' width={180} height={20} />
            <Skeleton variant='text' width={180} height={20} />
          </>
        ) : (
          <Box width={180}>
            <Avatar
              src={user.avatar || user.icon || ''}
              alt='User Avatar'
              sx={{ width: '40px', height: '40px', marginBottom: '8px' }}
            />
            <Typography variant='subtitle1'>{`${user.first_name} ${user.last_name}`}</Typography>
            <Typography variant='body2'>{user.company ?? 'Company'}</Typography>
            {enableEmails && !isPrivateUser && (
              <Typography variant='caption' display='block'>
                {user.email}
              </Typography>
            )}
            {enablePrivateMeetings && privateMeetingsValue && (
              <CallButton
                otherUser={
                  {
                    id: user.id,
                    first_name: user.first_name,
                    last_name: user.last_name,
                    avatar: user.avatar,
                    role: user.role,
                  } as CommunityUser
                }
                setAnchorEl={setAnchorEl}
              />
            )}

            <Button
              aria-label='view-full-profile'
              variant='contained'
              color='primary'
              size='small'
              sx={{ textTransform: 'none', mt: 1 }}
              onClick={(e) => {
                e.stopPropagation();
                onViewProfile(user.id);
              }}
              fullWidth
              disabled={user.role === RoleEnum.NUMBER_2}
            >
              View Full Profile
            </Button>
            {/* // TODO - message user */}
            {/* <Button variant='text' color='primary' size='small' sx={{ textTransform: 'none' }} onClick={()=>onMessageUser(userId)}>
                Message User
                </Button> */}
          </Box>
        )}
      </Paper>
    </Popover>
  );
});

export default UserQuickView;

export const CallButton = ({
  otherUser,
  setAnchorEl,
}: {
  otherUser: CommunityUser;
  setAnchorEl?: Dispatch<SetStateAction<HTMLElement | null>> | undefined;
}) => {
  const { startPrivateCall } = usePrivateCall();
  const { user } = useSettings();
  const isUserOnline: boolean = useIsUserOnline(otherUser.id);
  const { isStaff } = useSettings();
  const isCurrentUser = otherUser.id === user?.id;

  const handleStartPrivateCall = async () => {
    startPrivateCall({
      id: otherUser.id,
      first_name: otherUser.first_name,
      last_name: otherUser.last_name,
      avatar: otherUser.avatar,
    } as CommunityUser);
    setAnchorEl && setAnchorEl(null);
  };

  const isDisabled =
    !isUserOnline || isCurrentUser || otherUser.role === RoleEnum.NUMBER_2 || isStaff;
  const showTooltip = !isCurrentUser && isUserOnline;

  return (
    <WithOptionalTooltip show={showTooltip} title='Start a private call'>
      <IconButton
        aria-label='start-private-call'
        onClick={() => handleStartPrivateCall()}
        disabled={isDisabled}
      >
        <ConnectWithoutContactIcon />
      </IconButton>
    </WithOptionalTooltip>
  );
};
